/** Pure helpers of AddressSearchField (row texts, attribution, menu direction / height). */

export interface AddressSearchItemLike {
    text?: string;
    source?: string;
    value?: {
        street?: string | null;
        houseNumber?: string | null;
        postal?: string | null;
        city?: string | null;
    } | null;
}

export const ADDRESS_MENU_MAX_HEIGHT = 320;
export const ADDRESS_MENU_MIN_HEIGHT = 120;
const ROW_HEIGHT = 52;
const ATTRIBUTION_HEIGHT = 30;
const LIST_PADDING = 8;
/** Gap between field and menu plus a margin to the edge of the visible area */
const EDGE_GAP = 12;

/** Main line (street + house number) and second line (PLZ Ort) of a result row */
export function addressLines(item: AddressSearchItemLike | null | undefined): { main: string; sub: string } {
    const value = item?.value || {};
    const streetPart = [value.street, value.houseNumber].filter(Boolean).join(' ').trim();
    const cityPart = [value.postal, value.city].filter(Boolean).join(' ').trim();
    if (streetPart) {
        return {main: streetPart, sub: cityPart};
    }
    if (cityPart) {
        return {main: cityPart, sub: ''};
    }
    return {main: String(item?.text ?? ''), sub: ''};
}

/** Attribution shown once below the results, depending on the sources of the shown rows */
export function sourceAttribution(items: AddressSearchItemLike[] | null | undefined): string {
    const sources = new Set((items || []).map((item) => item?.source));
    const parts: string[] = [];
    if (sources.has('google-maps')) {
        parts.push('Bereitgestellt von Google Maps');
    }
    if (sources.has('osm')) {
        parts.push('© OpenStreetMap-Mitwirkende');
    }
    return parts.join(' · ');
}

export interface AddressMenuLayoutInput {
    fieldTop: number;
    fieldBottom: number;
    /** Visible part of the scroll container (dialog body) or the window */
    boxTop: number;
    boxBottom: number;
    rows: number;
    withAttribution: boolean;
    /** Current direction: kept while it still has room (no flipping back and forth while scrolling) */
    openUp: boolean;
}

/**
 * Opens below unless the room below is too small and there is more room above; height fits the chosen side.
 * fits = the wanted height has room on the chosen side (otherwise the menu should leave the scroll container).
 */
export function addressMenuLayout(input: AddressMenuLayoutInput): { openUp: boolean; maxHeight: number; fits: boolean } {
    const wanted = Math.min(
        ADDRESS_MENU_MAX_HEIGHT,
        Math.max(1, input.rows) * ROW_HEIGHT + (input.withAttribution ? ATTRIBUTION_HEIGHT : 0) + LIST_PADDING,
    );
    const below = Math.max(0, input.boxBottom - input.fieldBottom - EDGE_GAP);
    const above = Math.max(0, input.fieldTop - input.boxTop - EDGE_GAP);
    let openUp: boolean;
    if (input.openUp) {
        openUp = above >= wanted || above > below;
    } else {
        openUp = below < wanted && above > below;
    }
    const room = openUp ? above : below;
    const maxHeight = Math.round(Math.max(ADDRESS_MENU_MIN_HEIGHT, Math.min(ADDRESS_MENU_MAX_HEIGHT, room)));
    return {openUp, maxHeight, fits: room >= Math.min(wanted, ADDRESS_MENU_MIN_HEIGHT * 1.5)};
}
